import { useRouter } from '@/hooks/use-routing';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';

interface SidebarContextValue {
  invalidateConversations: () => void;
}

/**
 * Creates a new chat within an existing workspace (by cloning from a source chat).
 * Navigates to the new chat on success.
 */
export function useCreateInWorkspace(ctx: SidebarContextValue) {
  const router = useRouter();

  return trpc.chat.createInWorkspace.useMutation({
    onSuccess: (data) => {
      ctx.invalidateConversations();
      router.push(`/chat/${data.chatId}`);
    },
  });
}

/**
 * Creates an empty workspace with no chats.
 */
export function useCreateEmptyWorkspace(ctx: SidebarContextValue) {
  return trpc.chat.createEmptyWorkspace.useMutation({
    onSuccess: () => {
      ctx.invalidateConversations();
    },
  });
}

/**
 * Creates a new chat in an existing workspace by workspace ID.
 * Navigates to the new chat on success.
 */
export function useCreateChatInWorkspace(ctx: SidebarContextValue) {
  const router = useRouter();

  return trpc.chat.createChatInWorkspace.useMutation({
    onSuccess: (data) => {
      ctx.invalidateConversations();
      router.push(`/chat/${data.chatId}`);
    },
  });
}

/**
 * Renames a workspace (updates title only).
 */
export function useRenameWorkspace(ctx: SidebarContextValue) {
  return trpc.chat.updateWorkspaceTitle.useMutation({
    onSuccess: () => {
      ctx.invalidateConversations();
    },
  });
}

interface UploadNotebooksParams {
  workspaceDir: string;
  files: File[];
}

export function useUploadNotebooks() {
  const utils = trpc.useUtils();
  const mutation = trpc.notebooks.upload.useMutation({
    onSuccess: () => {
      utils.notebooks.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  return {
    ...mutation,
    mutate: ({ workspaceDir, files }: UploadNotebooksParams) => {
      const formData = buildNotebookUploadFormData({ workspaceDir, files });
      mutation.mutate(formData);
    },
    mutateAsync: async ({ workspaceDir, files }: UploadNotebooksParams) => {
      const formData = buildNotebookUploadFormData({ workspaceDir, files });
      return mutation.mutateAsync(formData);
    },
  };
}

function buildNotebookUploadFormData({ workspaceDir, files }: UploadNotebooksParams): FormData {
  const formData = new FormData();
  formData.append('workspaceDir', workspaceDir);
  files.forEach((file) => formData.append('notebooks', file));
  return formData;
}

/**
 * Hook for syncing notebooks from Jupyter filesystem to the database.
 * Creates notebook records for any .ipynb files in the workspace that aren't tracked.
 */
export function useSyncNotebooks() {
  const utils = trpc.useUtils();

  return trpc.notebooks.sync.useMutation({
    onSuccess: () => {
      utils.notebooks.invalidate();
    },
  });
}
